import React, { useState, useEffect } from 'react';
import authApi from '../../services/authApi';
import {
  Container,
  Typography,
  Button,
  IconButton,
  Box,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { userService } from '../../services/userService';
import UserDialog from './UserDialog';
import DataTable from '../common/DataTable';
import { useTheme, useMediaQuery } from '@mui/material';
import NavigationDrawer from '../Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = authApi.getToken();
      const data = await userService.getUsers(token);
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    setSaveError('');
    setCurrentUser(user);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSaveError('');
    setOpenDialog(false);
    setCurrentUser(null);
  };

  const handleSaveUser = async (userData) => {
    try {
      setSaveError('');
      const token = authApi.getToken();
      const identityId = userData.identity_id;
      if (identityId) {
        await userService.updateUser(token, identityId, {
          first_name: userData.first_name,
          last_name: userData.last_name,
          role: userData.role,
          is_active: userData.is_active
        });
        if (userData.newPassword && userData.newPassword.trim()) {
          await userService.setUserPassword(
            token,
            identityId,
            userData.newPassword,
            userData.must_change_on_reset
          );
        }
      } else {
        await userService.createUser(token, {
          email: userData.email,
          first_name: userData.first_name,
          last_name: userData.last_name,
          role: userData.role,
          password: userData.password,
          is_active: userData.is_active
        });
      }
      handleCloseDialog();
      await fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.code ||
        error.message ||
        'Failed to save user';
      setSaveError(typeof msg === 'string' ? msg : 'Failed to save user');
    }
  };

  const columns = [
    { id: 'email', label: 'Email' },
    { id: 'first_name', label: 'First Name' },
    { id: 'last_name', label: 'Last Name' },
    { id: 'role', label: 'Role' },
    {
      id: 'is_active',
      label: 'Active',
      render: (row) => (row.is_active === false ? 'No' : 'Yes')
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="SharePoint configuration (user account)">
            <IconButton
              onClick={() =>
                navigate(`/admin/sharepoint-config/${encodeURIComponent(row.email)}`, {
                  state: {
                    displayName: [row.first_name, row.last_name].filter(Boolean).join(' ').trim()
                  }
                })
              }
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit user">
            <IconButton onClick={() => handleOpenDialog(row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={''}
        setActiveSection={() => {}}
        navigate={navigate}
      />
      
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>
        <Container>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4">User Management</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              New User
            </Button>
          </Box>

          <DataTable
            columns={columns}
            data={users}
            loading={loading}
            searchable
            searchFields={['email', 'first_name', 'last_name', 'role']}
          />

          <UserDialog
            open={openDialog}
            onClose={handleCloseDialog}
            onSave={handleSaveUser}
            user={currentUser}
            saveError={saveError}
          />
        </Container>
      </Box>
    </Box>
  );
};

export default UserManagement; 